import auth from "@/middleware/auth";
import hasRoles from "@/middleware/hasRoles";
import isActivatedUser from "@/middleware/isActivatedUser";
import checkIsVideoInWindow from "@/middleware/checkIsVideoInWindow";
import checkIsVideoMounted from "@/middleware/checkIsVideoMounted";
import clearCartData from "@/middleware/clearCartData";
import checkAccountCompletion from "@/middleware/checkAccountCompletion";

const mainPageBreadCrumb = [{ name: "layouts.main_page", link: "Dashboard" }];

export default {
  path: "",
  name: "DashboardDoctor",
  meta: {
    middlewares: [
      auth,
      hasRoles,
      isActivatedUser,
      checkIsVideoInWindow,
      checkIsVideoMounted,
      checkAccountCompletion,
    ],
    roles: ["doctor"],
  },
  children: [
    {
      path: "account",
      name: "SettingsAccount",
      meta: {
        middlewares: [
          auth,
          hasRoles,
          checkIsVideoInWindow,
          checkIsVideoMounted,
          checkAccountCompletion,
        ],
        roles: ["doctor"],
      },
      component: () =>
        import(
          /* webpackChunkName: "doctor-account" */ "@/components/Layouts/Doctor/DoctorAccount.vue"
        ),
      children: [
        {
          path: "profile",
          name: "Profile",
          component: () =>
            import(
              /* webpackChunkName: "account-profile" */ "@/views/Doctor/settings/Profile.vue"
            ),
        },
        {
          path: "settings",
          name: "SettingsAccountProfile",
          meta: {
            roles: ["doctor", "admin"],
          },
          component: () =>
            import(
              /* webpackChunkName: "account-settings" */ "@/views/Doctor/settings/Account.vue"
            ),
        },
        {
          path: "services",
          name: "SettingsAccountServices",
          component: () =>
            import(
              /* webpackChunkName: "account-settings" */ "@/views/Doctor/settings/Services.ts.vue"
            ),
        },
        {
          path: "patient_appointment_hours",
          name: "SettingsAccountAppointmentHours",
          component: () =>
            import(
              /* webpackChunkName: "account-settings" */ "@/views/Doctor/settings/AppointmentHours.ts.vue"
            ),
        },
        {
          path: "patient_sms_notifications",
          name: "SettingsAccountSmsNotifications",
          component: () =>
            import(
              /* webpackChunkName: "account-settings" */ "@/views/Doctor/settings/SmsNotifications.ts.vue"
            ),
        },
      ],
    },
    {
      path: "quick-call/:uid",
      name: "QuickCall",
      component: () =>
        import(
          /* webpackChunkName: "home" */ "@/views/Doctor/QuickCall.ts.vue"
        ),
    },
    {
      path: "call/video/:uid",
      name: "CallVideo",
      component: () =>
        import(
          /* webpackChunkName: "videocall" */ "@/views/Doctor/Call.ts.vue"
        ),
    },
    {
      path: "call/phone/:uid",
      name: "CallPhone",
      component: () =>
        import(
          /* webpackChunkName: "videocall" */ "@/views/Doctor/Call.ts.vue"
        ),
    },
    {
      path: "call/:uid/details",
      name: "CallDetails",
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "call-details" */ "@/views/Doctor/CallDetails.vue"
        ),
    },
    {
      path: "/calendar",
      name: "DoctorCalendar",
      component: () =>
        import(
          /* webpackChunkName: "doctor-calendar" */ "@/views/Doctor/Calendar.ts.vue"
        ),
    },
    {
      path: "/visits",
      name: "DoctorVisits",
      component: () =>
        import(
          /* webpackChunkName: "doctor-visits" */ "@/views/Doctor/Visits.vue"
        ),
    },
    {
      path: "settlements",
      name: "DoctorSettlements",
      component: () =>
        import(
          /* webpackChunkName: "doctor-settlements" */ "@/views/Doctor/Settlements.vue"
        ),
    },
    {
      path: "referral-create",
      name: "DoctorReferralCreate",
      meta: {
        middlewares: [auth, hasRoles, isActivatedUser, clearCartData],
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.examintons_list" },
        ],
      },
      component: () =>
        import(
          /* webpackChunkName: "doctor-referral-create" */ "@/views/Doctor/ReferralCreate.vue"
        ),
    },
    {
      path: "referral-visit",
      name: "DoctorReferralVisit",
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.examintons_list" },
        ],
      },
      component: () =>
        import(
          /* webpackChunkName: "doctor-referral-add-visit" */ "@/views/Doctor/ReferralAddVisit.vue"
        ),
    },
    {
      path: "tests-to-describe/:describeId",
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.examintons_list" },
        ],
      },
      children: [
        {
          path: "referral-create",
          name: "TestToDescribeReferralCreate",
          props: true,
          component: () =>
            import(
              /* webpackChunkName: "doctor-referral-create" */ "@/views/Doctor/ReferralCreate.vue"
            ),
        },
        {
          path: "referral-visit",
          name: "TestToDescribeReferralVisit",
          props: true,
          component: () =>
            import(
              /* webpackChunkName: "doctor-referral-add-visit" */ "@/views/Doctor/ReferralAddVisit.vue"
            ),
        },
      ],
    },
    {
      path: "meeting",
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.examintons_list" },
        ],
      },
      children: [
        {
          path: "video/:meetingId",
          children: [
            {
              path: "referral-create",
              name: "MeetingReferralCreateVideo",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "doctor-referral-create" */ "@/views/Doctor/ReferralCreate.vue"
                ),
            },
            {
              path: "referral-visit",
              name: "MeetingReferralVisitVideo",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "doctor-referral-add-visit" */ "@/views/Doctor/ReferralAddVisit.vue"
                ),
            },
          ],
        },
        {
          path: "phone/:meetingId",
          meta: {
            breadcrumb: [
              ...mainPageBreadCrumb,
              { name: "layouts.examintons_list" },
            ],
          },
          children: [
            {
              path: "referral-create",
              name: "MeetingReferralCreatePhone",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "doctor-referral-create" */ "@/views/Doctor/ReferralCreate.vue"
                ),
            },
            {
              path: "referral-visit",
              name: "MeetingReferralVisitPhone",
              props: true,
              component: () =>
                import(
                  /* webpackChunkName: "doctor-referral-add-visit" */ "@/views/Doctor/ReferralAddVisit.vue"
                ),
            },
          ],
        },
      ],
    },
    {
      path: "referral-edit/:id",
      name: "DoctorReferralEdit",
      component: () =>
        import(
          /* webpackChunkName: "doctor-referral-edit" */ "@/views/Doctor/ReferralCreate.vue"
        ),
    },
    {
      path: "referral-history",
      name: "DoctorReferralHistory",
      component: () =>
        import(
          /* webpackChunkName: "doctor-referral-history" */ "@/views/Doctor/ReferralHistory.vue"
        ),
    },
    {
      path: "tests-to-describe",
      name: "TestsToDescribe",
      component: () =>
        import(
          /* webpackChunkName: "tests-to-describe" */ "@/views/Doctor/TestsToDescribe.vue"
        ),
    },
    {
      path: "tests-to-describe/describe/:id",
      name: "TestsToDescribeDescribe",
      component: () =>
        import(
          /* webpackChunkName: "tests-to-describe-describe" */ "@/views/Doctor/TestsToDescribeDescribe.vue"
        ),
    },
    {
      path: "own-package/:id",
      name: "DoctorOwnPackage",
      props: (route) => ({ id: route.params.id * 1 }),
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.examintons_list", link: "DoctorReferralCreate" },
          { name: "layouts.own_packages", link: "DoctorReferralCreate" },
          { name: ":name" },
        ],
      },
      component: () =>
        import(
          /* webpackChunkName: "doctor-own-package" */ "@/views/Doctor/OwnPackage.vue"
        ),
    },
    {
      path: "referral/facility/:id?",
      name: "DoctorReferralFacility",
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "doctor-referral-facility" */ "@/views/Auth/Doctor/Facility.vue"
        ),
    },
  ],
};
